// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit } from '@evolvemep/api-ts';
import { TextInput, type TextInputProps, type TextareaProps, Textarea, ComboboxClearButton } from '@mantine/core';

type Props = {
  value?: string | null;
  onChange: (value: string) => void;
  onSubmit?: () => void;
};

export const TextInputWrapped = ({
  onSubmit,
  value,
  onChange,
  clearable,
  ...props
}: StrongOmit<TextInputProps, keyof Props> &
  Props & {
    clearable?: boolean;
  }) => (
  <TextInput
    rightSection={clearable && !!value ? <ComboboxClearButton onClear={() => onChange('')} /> : undefined}
    {...props}
    value={value ?? ''}
    onChange={(e) => onChange(e.currentTarget.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' && onSubmit) {
        onSubmit();
        e.preventDefault();
        e.stopPropagation();
      }
      props.onKeyDown?.(e);
    }}
  />
);

export const TextareaWrapped = ({
  onSubmit,
  value,
  onChange,
  ...props
}: StrongOmit<TextareaProps, keyof Props> & Props) => (
  <Textarea
    autosize
    minRows={2}
    maxRows={10}
    {...props}
    value={value ?? ''}
    onChange={(e) => onChange(e.currentTarget.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' && e.ctrlKey) onSubmit?.();
      props.onKeyDown?.(e);
    }}
    onFocus={(e) => {
      e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length);
      props.onFocus?.(e);
    }}
  />
);
